import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, useTheme, useMediaQuery, Menu, MenuItem, IconButton, Button } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import axios from 'axios';

// Interfaces TypeScript
interface ActivityItem {
  id: number;
  title: string;
  description?: string;
  image_url: string;
  alt_text?: string;
  order_index: number;
  category?: string;
}

interface ActivitySection {
  id: number;
  name: string;
  title: string;
  description?: string;
  section_type: 'vie_academique' | 'moments_forts';
  order_index: number;
  items: ActivityItem[];
}

interface PageContent {
  title?: string;
  subtitle1?: string;
  subtitle2?: string;
  background_image?: string;
}

// Composant de navigation avec onglets (réutilisé de Home.tsx)
const NavigationBar = () => {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [scolariteMenuAnchor, setScolariteMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleScolariteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setScolariteMenuAnchor(event.currentTarget);
  };

  const handleScolariteMenuClose = () => {
    setScolariteMenuAnchor(null);
  };

  const handleContactsClick = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationItems = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    { 
      label: 'SCOLARITE', 
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ];

  return (
    <Box 
      position="absolute" 
      sx={{ 
        bgcolor: 'transparent',
        boxShadow: 'none',
        zIndex: 10,
        top: '60px',
        width: '100%'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: { xs: 1, md: 2 },
          px: { xs: 2, md: 0 },
          minHeight: { xs: 56, md: 64 }
        }}>
          {/* Logo - Masqué sur mobile */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              border: '2px solid #87CEEB',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'white',
              overflow: 'hidden',
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <img
                src="/img/pages/logologo.webp"
                alt="La Maison des Enfants La petite Académie"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          </Box>

          {/* Navigation Desktop */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              gap: { md: 2, lg: 3 },
              alignItems: 'center'
            }}>
              {navigationItems.map((item) => (
                <Box key={item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <Typography
                        onClick={handleScolariteMenuOpen}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: { md: 12, lg: 14 },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          px: { md: 1.5, lg: 2 },
                          py: 1,
                          borderBottom: '2px solid transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#FF9800',
                            borderBottom: '2px solid #FF9800',
                          }
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Menu
                        anchorEl={scolariteMenuAnchor}
                        open={Boolean(scolariteMenuAnchor)}
                        onClose={handleScolariteMenuClose}
                        sx={{
                          '& .MuiPaper-root': {
                            bgcolor: 'rgba(23, 128, 194, 0.95)',
                            color: 'white',
                            mt: 1,
                            backdropFilter: 'blur(10px)',
                            minWidth: 200
                          }
                        }}
                      >
                        {item.submenu?.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={handleScolariteMenuClose}
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : item.isScroll ? (
                    <Typography
                      onClick={handleContactsClick}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: item.label === 'NOS ACTIVITES' ? '#FF9800' : 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: item.label === 'NOS ACTIVITES' ? '2px solid #FF9800' : '2px solid transparent',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Menu Mobile */}
          {isMobile && (
            <>
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    bgcolor: 'rgba(23, 128, 194, 0.95)',
                    color: 'white',
                    mt: 1,
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                {navigationItems.map((item) => (
                  <Box key={item.label}>
                    {item.hasSubmenu ? (
                      <>
                        <MenuItem
                          onClick={handleScolariteMenuOpen}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 14,
                            textTransform: 'uppercase',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          {item.label}
                          <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </MenuItem>
                        <Menu
                          anchorEl={scolariteMenuAnchor}
                          open={Boolean(scolariteMenuAnchor)}
                          onClose={handleScolariteMenuClose}
                          sx={{
                            '& .MuiPaper-root': {
                              bgcolor: 'rgba(23, 128, 194, 0.95)',
                              color: 'white',
                              mt: 1,
                              backdropFilter: 'blur(10px)',
                              minWidth: 200
                            }
                          }}
                        >
                          {item.submenu?.map((subItem) => (
                            <MenuItem
                              key={subItem.label}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={() => {
                                handleScolariteMenuClose();
                                handleMobileMenuClose();
                              }}
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: 14,
                                textTransform: 'uppercase',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                }
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : item.isScroll ? (
                      <MenuItem
                        onClick={() => {
                          handleContactsClick();
                          handleMobileMenuClose();
                        }}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMobileMenuClose}
                        sx={{
                          color: item.label === 'NOS ACTIVITES' ? '#FF9800' : 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    )}
                  </Box>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

// Section Hero basée sur l'image
const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100vh',
      backgroundImage: 'url(/img/pages/ac.jpg)', // Image de fond du bâtiment
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {/* Overlay sombre semi-transparent */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.6)', // Overlay sombre comme sur l'image
        zIndex: 1
      }} />

      {/* Texte de fond "La Maison des Enfants la Petite Académie" */}
      <Typography
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'rgba(0, 0, 0, 0.8)',
          fontSize: { xs: 24, md: 36, lg: 48 },
          fontWeight: 'bold',
          textAlign: 'center',
          zIndex: 1,
          fontFamily: 'Arial, sans-serif',
          letterSpacing: 1,
          textShadow: 'none'
        }}
      >
      </Typography>

      {/* Contenu principal - Texte blanc en avant-plan */}
      <Box sx={{
        position: 'relative',
        zIndex: 3,
        textAlign: 'center',
        color: 'white'
      }}>
        {/* Titre principal "NOS ACTIVITES" */}
        <Typography
          variant="h1"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: 32, md: 48, lg: 64 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            mb: 3,
            textTransform: 'uppercase',
            letterSpacing: 2,
            fontFamily: 'Arial, sans-serif'
          }}
        >
          NOS ACTIVITES
        </Typography>
        
        {/* Sous-titres */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: 18, md: 24, lg: 28 },
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            VIE ACADÉMIQUE
          </Typography>
          
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: 18, md: 24, lg: 28 },
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            MOMENTS FORTS
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Section VIE ACADÉMIQUE avec les données dynamiques
const ActivitiesContent = ({ section }: { section: ActivitySection }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Fonction pour obtenir les catégories uniques avec leur première activité
  const getUniqueCategories = (items: ActivityItem[]): { category: string; firstItem: ActivityItem }[] => {
    console.log('🔍 ActivitiesContent - getUniqueCategories - Items reçus:', items);
    
    const categoryMap = new Map<string, ActivityItem>();
    
    items.forEach(item => {
      // Normaliser la catégorie en trimant les espaces
      const category = (item.category || 'Général').trim();
      console.log(`  - Item: ${item.title}, Catégorie: "${category}"`);
      if (!categoryMap.has(category)) {
        categoryMap.set(category, item);
        console.log(`    ✅ Ajouté à la map: "${category}"`);
      } else {
        console.log(`    ⚠️ Catégorie déjà présente: "${category}"`);
      }
    });
    
    const result = Array.from(categoryMap.entries()).map(([category, firstItem]) => ({
      category,
      firstItem
    }));
    
    console.log('🔍 ActivitiesContent - getUniqueCategories - Résultat:', result);
    console.log('🔍 ActivitiesContent - Nombre de catégories uniques:', result.length);
    return result;
  };

  const getImageUrl = (imageUrl: string) => {
    console.log('🔍 ActivitiesContent - getImageUrl - URL originale:', imageUrl);
    
    if (imageUrl.startsWith('http')) {
      console.log('✅ URL complète détectée:', imageUrl);
      return imageUrl;
    }
    
    // Si l'URL commence par /img/pages, utiliser le domaine du frontend
    if (imageUrl.startsWith('/img/pages/')) {
      const finalUrl = `https://lapetiteacademie.ci${imageUrl}`;
      console.log('✅ URL /img/pages convertie:', finalUrl);
      return finalUrl;
    }
    
    // Si l'URL commence par /uploads/pages/, utiliser la nouvelle route API
    if (imageUrl.startsWith('/uploads/pages/')) {
      const filename = imageUrl.split('/').pop();
      const finalUrl = `https://lapetiteacademie.ci/api/dynamic-pages/images/${filename}`;
      console.log('✅ URL /uploads/pages convertie:', finalUrl);
      return finalUrl;
    }
    
    // Si l'URL commence par /uploads, utiliser le domaine du backend
    if (imageUrl.startsWith('/uploads/')) {
      const finalUrl = `https://lapetiteacademie.ci${imageUrl}`;
      console.log('✅ URL /uploads convertie:', finalUrl);
      return finalUrl;
    }
    
    // Par défaut, ajouter le domaine du backend
    const finalUrl = `https://lapetiteacademie.ci${imageUrl}`;
    console.log('✅ URL par défaut:', finalUrl);
    return finalUrl;
  };

  const handleImageClick = (activity: ActivityItem) => {
    // Rediriger vers la page de galerie avec la catégorie de l'activité
    // Normaliser la catégorie en trimant les espaces pour correspondre au filtrage
    const category = (activity.category || section.id.toString()).trim();
    // Encoder la catégorie pour gérer les caractères spéciaux comme "/"
    const encodedCategory = encodeURIComponent(category);
    navigate(`/activity-gallery/${encodedCategory}`);
  };

  return (
    <Box sx={{
      bgcolor: 'white',
      py: { xs: 6, md: 8 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Motifs décoratifs en arrière-plan */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/doodles.png)', // Image avec nuages, voitures, étoiles
        backgroundSize: '200px',
        backgroundRepeat: 'repeat',
        opacity: 0.1,
        zIndex: 0
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Titre principal */}
        <Typography
          variant="h2"
          sx={{
            color: '#1780c2',
            fontWeight: 700,
            fontSize: { xs: 28, md: 36, lg: 42 },
            textAlign: 'center',
            mb: 6,
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {section.title}
        </Typography>

        {/* Debug: Afficher le nombre de catégories uniques */}
        {(() => {
          console.log(`🔍 ActivitiesContent - Nombre d'items dans la section:`, section.items.length);
          console.log(`🔍 ActivitiesContent - Catégories uniques trouvées:`, getUniqueCategories(section.items).length);
          return null;
        })()}
        
        {/* Grille des éléments - Une image représentative par catégorie */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: `repeat(${Math.min(getUniqueCategories(section.items).length, 4)}, 1fr)` 
          },
          gap: { xs: 3, md: 4 },
          alignItems: 'start'
        }}>
          {getUniqueCategories(section.items).map((categoryData, index) => {
            const { category, firstItem } = categoryData;
            console.log(`🔍 ActivitiesContent - Rendu catégorie ${index + 1}:`, category, firstItem.title);
            return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              {/* Image de l'activité - Cliquable */}
              <Box 
                onClick={() => handleImageClick(firstItem)}
                sx={{
                  width: '100%',
                  height: { xs: 200, sm: 250, md: 300 },
                  overflow: 'hidden',
                  borderRadius: 2,
                  border: '3px solid #1780c2',
                  mb: 2,
                  position: 'relative',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, boxShadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                    border: '3px solid #FF9800'
                  }
                }}
              >
                <img
                  src={getImageUrl(firstItem.image_url)}
                  alt={firstItem.alt_text || firstItem.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    // Image de fallback en cas d'erreur
                    e.currentTarget.src = `https://picsum.photos/400/300?random=${firstItem.id}`;
                  }}
                />
                
                {/* Overlay au survol */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 1
                  }
                }}>
                  <PhotoLibraryIcon sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              </Box>

              {/* Nom de la catégorie */}
              <Typography
                variant="h6"
                sx={{
                  color: '#1780c2',
                  fontWeight: 700,
                  fontSize: { xs: 14, md: 16 },
                  fontFamily: 'Arial, sans-serif',
                  mb: 1,
                  textAlign: 'center'
                }}
              >
                {category}
              </Typography>

              {/* Titre de l'activité représentative - Cliquable */}
              <Button
                component={RouterLink}
                to={`/activites/${firstItem.id}`}
                sx={{
                  color: '#1780c2',
                  fontWeight: 600,
                  fontSize: { xs: 12, md: 14 },
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.3,
                  textAlign: 'center',
                  mb: 1,
                  minHeight: { xs: 'auto', md: '40px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  textDecorationColor: '#1780c2',
                  p: 1,
                  '&:hover': {
                    bgcolor: 'transparent',
                    textDecoration: 'underline',
                    textDecorationColor: '#FF9800',
                    color: '#FF9800'
                  }
                }}
              >
                {firstItem.title}
              </Button>

              
            </Box>
            );
          })}
        </Box>

        
      </Container>
      
      {/* Galerie modale */}
    </Box>
  );
};

// Section MOMENTS FORTS avec les données dynamiques
const MomentsFortsSection = ({ section }: { section: ActivitySection }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Fonction pour obtenir les catégories uniques avec leur première activité
  const getUniqueCategories = (items: ActivityItem[]): { category: string; firstItem: ActivityItem }[] => {
    console.log('🔍 MomentsFortsSection - getUniqueCategories - Items reçus:', items);
    
    const categoryMap = new Map<string, ActivityItem>();
    
    items.forEach(item => {
      // Normaliser la catégorie en trimant les espaces
      const category = (item.category || 'Général').trim();
      console.log(`  - Item: ${item.title}, Catégorie: "${category}"`);
      if (!categoryMap.has(category)) {
        categoryMap.set(category, item);
        console.log(`    ✅ Ajouté à la map: "${category}"`);
      } else {
        console.log(`    ⚠️ Catégorie déjà présente: "${category}"`);
      }
    });
    
    const result = Array.from(categoryMap.entries()).map(([category, firstItem]) => ({
      category,
      firstItem
    }));
    
    console.log('🔍 MomentsFortsSection - getUniqueCategories - Résultat:', result);
    console.log('🔍 MomentsFortsSection - Nombre de catégories uniques:', result.length);
    return result;
  };

  const getImageUrl = (imageUrl: string) => {
    console.log('🔍 MomentsFortsSection - getImageUrl - URL originale:', imageUrl);
    
    if (imageUrl.startsWith('http')) {
      console.log('✅ URL complète détectée:', imageUrl);
      return imageUrl;
    }
    
    // Si l'URL commence par /img/pages, utiliser le domaine du frontend
    if (imageUrl.startsWith('/img/pages/')) {
      const finalUrl = `https://lapetiteacademie.ci${imageUrl}`;
      console.log('✅ URL /img/pages convertie:', finalUrl);
      return finalUrl;
    }
    
    // Si l'URL commence par /uploads/pages/, utiliser la nouvelle route API
    if (imageUrl.startsWith('/uploads/pages/')) {
      const filename = imageUrl.split('/').pop();
      const finalUrl = `https://lapetiteacademie.ci/api/dynamic-pages/images/${filename}`;
      console.log('✅ URL /uploads/pages convertie:', finalUrl);
      return finalUrl;
    }
    
    // Si l'URL commence par /uploads, utiliser le domaine du backend
    if (imageUrl.startsWith('/uploads/')) {
      const finalUrl = `https://lapetiteacademie.ci${imageUrl}`;
      console.log('✅ URL /uploads convertie:', finalUrl);
      return finalUrl;
    }
    
    // Par défaut, ajouter le domaine du backend
    const finalUrl = `https://lapetiteacademie.ci${imageUrl}`;
    console.log('✅ URL par défaut:', finalUrl);
    return finalUrl;
  };

  const handleImageClick = (moment: ActivityItem) => {
    // Rediriger vers la page de galerie avec la catégorie de l'activité
    // Normaliser la catégorie en trimant les espaces pour correspondre au filtrage
    const category = (moment.category || section.id.toString()).trim();
    // Encoder la catégorie pour gérer les caractères spéciaux comme "/"
    const encodedCategory = encodeURIComponent(category);
    navigate(`/activity-gallery/${encodedCategory}`);
  };

  return (
    <Box sx={{
      bgcolor: '#f8f9fa',
      pt: { xs: 6, md: 8 },
      pb: 0,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Motifs décoratifs en arrière-plan */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/doodles.png)', // Motifs légers
        backgroundSize: '200px',
        backgroundRepeat: 'repeat',
        opacity: 0.05,
        zIndex: 0
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Titre principal */}
        <Typography
          variant="h2"
          sx={{
            color: '#1780c2',
            fontWeight: 700,
            fontSize: { xs: 28, md: 36, lg: 42 },
            textAlign: 'center',
            mb: 6,
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {section.title}
        </Typography>

        {/* Debug: Afficher le nombre de catégories uniques */}
        {(() => {
          console.log(`🔍 MomentsFortsSection - Nombre d'items dans la section:`, section.items.length);
          console.log(`🔍 MomentsFortsSection - Catégories uniques trouvées:`, getUniqueCategories(section.items).length);
          return null;
        })()}
        
        {/* Grille des cartes - Une image représentative par catégorie */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: `repeat(${Math.min(getUniqueCategories(section.items).length, 4)}, 1fr)` 
          },
          gap: { xs: 3, md: 4 },
          alignItems: 'start'
        }}>
          {getUniqueCategories(section.items).map((categoryData, index) => {
            const { category, firstItem } = categoryData;
            console.log(`🔍 MomentsFortsSection - Rendu catégorie ${index + 1}:`, category, firstItem.title);
            return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              {/* Image de l'activité - Cliquable */}
              <Box 
                onClick={() => handleImageClick(firstItem)}
                sx={{
                  width: '100%',
                  height: { xs: 200, sm: 250, md: 300 },
                  overflow: 'hidden',
                  borderRadius: 2,
                  border: '2px solid #1780c2',
                  mb: 2,
                  position: 'relative',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, boxShadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                    border: '2px solid #FF9800'
                  }
                }}
              >
                <img
                  src={getImageUrl(firstItem.image_url)}
                  alt={firstItem.alt_text || firstItem.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    // Image de fallback en cas d'erreur
                    e.currentTarget.src = `https://picsum.photos/400/300?random=${firstItem.id}`;
                  }}
                />
                
                {/* Overlay au survol */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 1
                  }
                }}>
                  <PhotoLibraryIcon sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              </Box>

              {/* Nom de la catégorie */}
              <Typography
                variant="h6"
                sx={{
                  color: '#1780c2',
                  fontWeight: 700,
                  fontSize: { xs: 14, md: 16 },
                  fontFamily: 'Arial, sans-serif',
                  mb: 1,
                  textAlign: 'center'
                }}
              >
                {category}
              </Typography>

              {/* Titre de l'activité représentative - Cliquable */}
              <Button
                component={RouterLink}
                to={`/activites/${firstItem.id}`}
                sx={{
                  color: '#1780c2',
                  fontWeight: 600,
                  fontSize: { xs: 12, md: 14 },
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.3,
                  textAlign: 'center',
                  mb: 1,
                  minHeight: { xs: 'auto', md: '40px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  textDecorationColor: '#1780c2',
                  p: 1,
                  '&:hover': {
                    bgcolor: 'transparent',
                    textDecoration: 'underline',
                    textDecorationColor: '#FF9800',
                    color: '#FF9800'
                  }
                }}
              >
                {firstItem.title}
              </Button>

  
            </Box>
            );
          })}
        </Box>
      </Container>

      {/* Section décorative en bas avec motifs scolaires */}
      <Box sx={{
        bgcolor: '#1780c2',
        py: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Motifs scolaires en blanc */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/img/pages/1.jpg)', // Motifs scolaires (crayons, règles, formules)
          backgroundSize: '250px',
          backgroundRepeat: 'repeat',
          opacity: 0.3,
          zIndex: 0
        }} />

        
      </Box>
      
      {/* Galerie modale */}
    </Box>
  );
};

// Composant principal Activites
const Activites = () => {
  const [sections, setSections] = useState<ActivitySection[]>([]);
  const [pageContent, setPageContent] = useState<PageContent>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadPageContent = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://lapetiteacademie.ci/api/dynamic-pages/activities-page');
        
        if (response.data.status === 'success') {
          console.log('📊 Données reçues de l\'API:', response.data.data);
          console.log('📊 Sections:', response.data.data.sections);
          
          // Debug: Afficher les catégories pour chaque section
          response.data.data.sections?.forEach((section: any) => {
            console.log(`🔍 Section ${section.title} (${section.section_type}):`);
            console.log(`🔍 Nombre d'items dans cette section:`, section.items?.length || 0);
            section.items?.forEach((item: any) => {
              console.log(`  - ${item.title} (catégorie: ${item.category || 'Aucune'})`);
            });
          });
          
          setSections(response.data.data.sections || []);
          setPageContent(response.data.data.content || {});
        }
      } catch (error) {
        console.error('Erreur lors du chargement du contenu de la page:', error);
        setError('Erreur lors du chargement du contenu');
        // Utiliser des données par défaut en cas d'erreur
        setSections([
          {
            id: 1,
            name: 'vie_academique',
            title: 'VIE ACADÉMIQUE',
            description: 'Découvrez nos activités éducatives et pédagogiques',
            section_type: 'vie_academique',
            order_index: 1,
            items: [
              {
                id: 1,
                title: "Sortie au centre Abel de Grand Bassam",
                image_url: "/img/pages/65.jpg",
                alt_text: "Sortie éducative à Grand Bassam",
                order_index: 1
              },
              {
                id: 2,
                title: "Pleine forêt",
                image_url: "/img/pages/67.jpg",
                alt_text: "Activités en forêt",
                order_index: 2
              },
              {
                id: 3,
                title: "Tapis rouge",
                image_url: "/img/pages/24.jpg",
                alt_text: "Séance de conte sur tapis rouge",
                order_index: 3
              },
              {
                id: 4,
                title: "Initiation à la robotique",
                image_url: "/img/pages/107.jpg",
                alt_text: "Atelier robotique pour enfants",
                order_index: 4
              }
            ]
          },
          {
            id: 2,
            name: 'moments_forts',
            title: 'MOMENTS FORTS',
            description: 'Les événements marquants de notre école',
            section_type: 'moments_forts',
            order_index: 2,
            items: [
              {
                id: 5,
                title: "Cérémonie de graduation promotion 2025",
                image_url: "/img/pages/MTC.jpeg",
                alt_text: "Cérémonie de graduation",
                order_index: 1
              },
              {
                id: 6,
                title: "Cérémonie de graduation promotion 2025",
                image_url: "/img/pages/142.jpg",
                alt_text: "Brunch de fin d'année",
                order_index: 2
              },
              {
                id: 7,
                title: "Tapis rouge",
                image_url: "/img/pages/53.jpg",
                alt_text: "Journée des stars",
                order_index: 3
              },
              {
                id: 8,
                title: "Tapis rouge",
                image_url: "/img/pages/23.jpg",
                alt_text: "Piscine party",
                order_index: 4
              }
            ]
          }
        ]);
        setPageContent({
          title: 'NOS ACTIVITES',
          subtitle1: 'VIE ACADÉMIQUE',
          subtitle2: 'MOMENTS FORTS',
          background_image: '/img/pages/ac.jpg'
        });
      } finally {
        setLoading(false);
      }
    };

    loadPageContent();
  }, []);

  // Trouver les sections
  const vieAcademiqueSection = sections.find(s => s.section_type === 'vie_academique');
  const momentsFortsSection = sections.find(s => s.section_type === 'moments_forts');

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Activités Éducatives et Moments Forts - La Petite Académie Abidjan</title>
        <meta name="description" content="Découvrez les activités éducatives et moments forts de La Petite Académie à Abidjan : sorties éducatives, kermesse, contes, initiation robotique, graduation, brunch party, journée des stars, piscine party. Vie académique et moments de convivialité." />
        <meta name="keywords" content="activités éducatives Abidjan, sorties éducatives, kermesse école, contes enfants, initiation robotique, graduation maternelle, brunch party, journée des stars, piscine party, vie académique, moments forts école, La Petite Académie" />
        <meta property="og:title" content="Activités Éducatives et Moments Forts - La Petite Académie Abidjan" />
        <meta property="og:description" content="Découvrez les activités éducatives et moments forts de La Petite Académie à Abidjan : sorties éducatives, kermesse, contes, initiation robotique, graduation, brunch party." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lapetiteacademie.ci/activites" />
        <link rel="canonical" href="https://lapetiteacademie.ci/activites" />
      </Helmet>
      <Box sx={{ 
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <ContactBanner />
        <NavigationBar />
        <HeroSection />
        {vieAcademiqueSection && <ActivitiesContent section={vieAcademiqueSection} />}
        {momentsFortsSection && <MomentsFortsSection section={momentsFortsSection} />}
        <SiteFooter />
      </Box>
    </>
  );
};

export default Activites;
